import {
  Client,
  ClientRepresentationStatusEnum,
  PermissionEnum,
} from '@sr-sdks/api-sdk-axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { queryOptionsGetClientsPaginated } from '../../../api/workspaces/clientApi';
import { UNCONFIRMED_CLIENTS_FETCH_LIMIT } from '../../../components/ConfirmClients';
import { useActiveWorkspace } from '../../../hooks/useActiveWorkspace';
import { useCheckUserPermissions } from '../../../hooks/useCheckUserPermissions';
import { workspaceTypeIsAgent } from '../../../utils/helpers/common';
import {
  ConfirmClientsModal,
  UnconfirmedClientsAlert,
} from './ConfirmClientsPrompt';

const WorkspaceClientsAlert = ({ workspaceId }: { workspaceId: string }) => {
  const [activeWorkspace] = useActiveWorkspace();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewClients, setReviewClients] = useState<Client[]>([]);

  const canVerifyClients = useCheckUserPermissions(
    [PermissionEnum.VerifyClientRepresentation],
    undefined,
    workspaceId,
  );
  const shouldAsk = canVerifyClients && workspaceTypeIsAgent(activeWorkspace);

  // Same batch the legacy banner confirms: every unconfirmed client, paged on
  // screen by the modal (APP-2334).
  const unconfirmedClientsOptions = queryOptionsGetClientsPaginated({
    pageSize: UNCONFIRMED_CLIENTS_FETCH_LIMIT,
    representationStatus: ClientRepresentationStatusEnum.Unconfirmed,
    sortBy: 'created:asc',
    workspaceId,
  });

  const { data: unconfirmedClients } = useQuery({
    ...unconfirmedClientsOptions,
    enabled: shouldAsk,
    // No background polling: onSaved refreshes it after a confirm.
    refetchInterval: false,
  });

  const showAlert = shouldAsk && !!unconfirmedClients?.items.length;

  return (
    <>
      {showAlert && (
        <UnconfirmedClientsAlert
          workspaceName={activeWorkspace?.workspaceName ?? ''}
          onConfirm={() => {
            setReviewClients(unconfirmedClients.items);
            setIsModalOpen(true);
          }}
        />
      )}

      <ConfirmClientsModal
        clients={reviewClients}
        isOpen={isModalOpen}
        workspaceId={workspaceId}
        onClientsChange={setReviewClients}
        onClose={() => {
          setReviewClients([]);
          setIsModalOpen(false);
        }}
        // Not on close: Close can beat the updates, and the refetch would then
        // read the cached pre-update list. Every client list, not just this one.
        onSaved={() =>
          queryClient.invalidateQueries({
            queryKey: unconfirmedClientsOptions.queryKey.slice(0, 1),
          })
        }
      />
    </>
  );
};

/**
 * Asks agents to confirm unconfirmed clients, for the active workspace only.
 */
export const AlertConfirmClientsForWorkspace = () => {
  const { workspaceId } = useParams();

  if (!workspaceId) return null;

  // Keyed: Home stays mounted across a workspace switch, and an open flow must
  // not carry the old workspace's clients or post-save refresh into the new one.
  return <WorkspaceClientsAlert key={workspaceId} workspaceId={workspaceId} />;
};
